import { ComponentType } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import CrossIcon from '@atlaskit/icon/core/cross';
import { media } from '@atlaskit/primitives';

import { Adg4Button } from '@/components/adg4/button/src/button';
import TryIntent from '@/components/try-intent/src/try-intent';
import { JourneyLocalStoragePersona, useAnchorLocalStorage } from '@/utils/local-storage-hook';

import { useHeroState } from './util/hero-context';

export type ProductModalCardType = {
	readonly eyebrow: string;
	readonly title: string;
	readonly description: string;
	readonly image: string;
	readonly iconTitle?: string;
	readonly iconCollection: {
		readonly icon: React.ReactNode;
		readonly description: string;
	}[];
	readonly BackgroundImage: ComponentType;
	readonly ctaLink: string;
	readonly label: string;
	readonly personaData: JourneyLocalStoragePersona;
};

export const ProductModalCard = ({
	eyebrow,
	title,
	description,
	iconCollection,
	image,
	BackgroundImage,
	ctaLink,
	label,
	personaData,
}: ProductModalCardType) => {
	const intl = useIntl();
	const { closeModal } = useHeroState();
	const { setAnchorLocalStorage } = useAnchorLocalStorage();

	const handleClick = () => {
		// Set team/persona in local storage for various uses later in funnel
		setAnchorLocalStorage(personaData);
	};

	const buttonText = intl.formatMessage({
		id: 'jira-product-tour.hero-modal-cta-btn.btn-text',
		defaultMessage: 'Get started',
	});

	const iconTitle = intl.formatMessage({
		id: 'jira-product-tour.hero-modal-icon-title.title-text',
		defaultMessage: 'Collaborate with these features',
	});

	return (
		<div css={modalStyles}>
			<div css={modalImageStyles}>
				<div css={backgroundStyles}>
					<BackgroundImage />
				</div>
				{/* eslint-disable-next-line no-restricted-syntax */}
				<img src={image} css={imageStyles} alt="" />
			</div>
			<div css={modalContentStyles}>
				<div css={textSpaceStyles}>
					<button onClick={closeModal} css={closeModalButtonStyles}>
						<CrossIcon size="medium" label="Close" />
					</button>
					<div css={eyebrowStyles}>{eyebrow}</div>
					<div css={titleStyles}>{title}</div>
					<div css={descriptionStyles}>{description}</div>
					<div css={[seporatorStyles, displayNoneBelowLg]} />
					<div css={[iconTitleStyles, displayNoneBelowLg]}>{iconTitle}</div>
					<div css={iconContainerStyles}>
						{iconCollection.map((icon, index) => (
							<div css={iconAndTextStyles} key={index}>
								<div css={iconStyles}>{icon.icon}</div>
								<div css={iconDescriptionStyles}>{icon.description}</div>
							</div>
						))}
					</div>
					<div css={buttonSpaceStyles}>
						<TryIntent
							intentType={'button'}
							containerId={'jira-product-tour-overview-hero-modal-' + personaData.team}
							productKey={'jira-software.ondemand'}
							edition={'free'}
						>
							<Adg4Button
								onClick={handleClick}
								href={ctaLink}
								variant="primary"
								size="small"
								color="blue"
								dataEventProps={{
									'data-product-key': 'jira',
									'data-event': 'clicked',
									'data-event-component': 'button',
									'data-event-container': 'heroSection',
									'data-label': 'get-started',
									'data-label-english': 'Get Started',
									'data-track-event': `jira-pt.hero-modal-${label}.get-started`,
								}}
							>
								{buttonText}
							</Adg4Button>
						</TryIntent>
					</div>
				</div>
			</div>
		</div>
	);
};

const modalStyles = css({
	display: 'flex',
	borderRadius: '22px',
	// eslint-disable-next-line local/ensure-wac-token-usage
	background: 'var(--Neutral-Light-200, #F0F1F2)',
	boxShadow:
		// eslint-disable-next-line local/ensure-wac-token-usage
		'0px 0px 8px 0px rgba(9, 30, 66, 0.16), 0px 0px 1px 0px rgba(9, 30, 66, 0.12)',
	justifyContent: 'flex-end',
	width: '80%',
	height: '80%',
	[media.above.lg]: {
		height: '80vh',
		width: '100%',
	},
	[media.below.lg]: {
		flexDirection: 'column-reverse',
		// eslint-disable-next-line local/ensure-wac-token-usage
		backgroundColor: '#FFF',
	},
	[media.below.sm]: {
		height: '90%',
		width: '80%',
	},
});

const modalImageStyles = css({
	flexGrow: '1',
	position: 'relative',
	display: 'grid',
	justifyItems: 'center',
	alignItems: 'center',
});

const imageStyles = css({
	maxHeight: '80%',
	maxWidth: '75%',
	position: 'absolute',
	[media.below.sm]: {
		maxWidth: '80%',
		maxHeight: '80%',
	},
});

const backgroundStyles = css({
	height: '90%',
	width: '90%',
	position: 'absolute',
	display: 'flex',
	justifyContent: 'center',
	[media.below.sm]: {
		width: '90%',
		height: '90%',
	},
});

const modalContentStyles = css({
	width: '367px',
	borderRadius: '22px',
	// eslint-disable-next-line local/ensure-wac-token-usage
	background: '#FFF',
	[media.below.lg]: {
		width: '100%',
	},
});

const textSpaceStyles = css({
	padding: '0px 31px',
});

const closeModalButtonStyles = css({
	display: 'flex',
	justifyContent: 'flex-end',
	width: '100%',
	padding: '0',
	backgroundColor: 'transparent',
	border: 'none',
	marginBlockStart: '16px',
	cursor: 'pointer',
});

const eyebrowStyles = css({
	// eslint-disable-next-line local/ensure-wac-token-usage
	color: 'var(--Primary-Black, var(--DarkNeutral-Opaque-DarkNeutral-100, #101214))',
	fontFamily: 'var(--atlassian-recommended-system-fonts)',
	fontSize: '14px',
	fontWeight: '400',
	lineHeight: '18px',
	letterSpacing: '1px',
	textTransform: 'uppercase',
	paddingBlockStart: '11px',
	[media.below.sm]: {
		fontSize: '12px',
		lineHeight: '16px',
	},
});

const titleStyles = css({
	// eslint-disable-next-line local/ensure-wac-token-usage
	color: 'var(--Primary-Black, var(--DarkNeutral-Opaque-DarkNeutral-100, #101214))',
	fontFamily: 'var(--atlassian-recommended-system-fonts)',
	fontSize: '32px',
	fontWeight: '700',
	lineHeight: '38px',
	marginBlockStart: '5px',
	[media.below.sm]: {
		fontSize: '24px',
		lineHeight: '28.8px',
	},
});

const descriptionStyles = css({
	marginBlockStart: '23px',
	[media.below.sm]: {
		fontSize: '16px',
		lineHeight: '24px',
		marginBlockEnd: '16px',
	},
});

const seporatorStyles = css({
	height: '1px',
	// eslint-disable-next-line local/ensure-wac-token-usage
	background: '#96999E',
	marginBlockStart: '32px',
});

const iconTitleStyles = css({
	marginBlockStart: '32px',
	marginBlockEnd: '9px',
	// eslint-disable-next-line local/ensure-wac-token-usage
	color: '#000',
	fontSize: '16px',
	fontWeight: '400',
	lineHeight: '24px',
	width: '241px',
});

const iconDescriptionStyles = css({
	// eslint-disable-next-line local/ensure-wac-token-usage
	color: '#000',
	fontSize: '14px',
	fontWeight: '400',
	lineHeight: '20px',
});

const iconContainerStyles = css({
	display: 'inline-flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: '8px',
	[media.below.sm]: {
		gap: '4px',
	},
});

const iconAndTextStyles = css({
	display: 'flex',
});

const iconStyles = css({
	width: '24px',
	height: '24px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const buttonSpaceStyles = css({
	paddingBlockStart: '32px',
	[media.below.sm]: {
		paddingBlockStart: '16px',
		paddingBlockEnd: '16px',
	},
});

const displayNoneBelowLg = css({
	[media.below.lg]: {
		display: 'none',
	},
});
